/**
 * @file issue-groups.ts
 * @description Какая группа настроек секции отвечает за замечание проверки
 * (phase19.16, SB122; ревью партии SB122): у свёрнутой группы с ошибкой в
 * заголовке ✕, после неудачного «Сохранить блок» раскрывается первая
 * группа с ошибкой. Замечание адресовано путём в payload
 * (`columns.items.0.elements.2.label`, `look.space.mergeWithPrev`).
 *
 * Группы и их пути — реестрами (новая группа = запись, не ветка):
 *  - `LOOK_ISSUE_RULES` — вкладка «Оформление» секции;
 *  - `CONTENT_ISSUE_RULES` — вкладка «Содержимое»;
 *  - `ELEMENT_ISSUE_RULES` — панель элемента (пути от пути элемента).
 * Правила проверяются по порядку: первое подходящее забирает замечание
 * (у элемента «Цвет и шрифт» и «Расположение» раньше «Содержимого»).
 * SB120 этап 1: тем же реестром — предупреждения об оформлении
 * (`sectionLookWarnings`, «!» у группы) и переход из списка замечаний.
 */

/** Правило: группа и пути её полей. */
export interface IssueGroupRule<G extends string> {
  group: G;
  /** Путь и всё под ним; `""` — всё под базовым путём. */
  under?: readonly string[];
  /** Только сам путь (ошибка списка целиком, без его элементов). */
  exact?: readonly string[];
  /** Полный путь по образцу (поля колонки `columns.items.N.bg` — во вкладке «Оформление», SB120). */
  match?: RegExp;
  /** Полный путь по образцу — не наш, даже если подходит `under`. */
  exclude?: RegExp;
}

/** Оформление колонки (фон, положение, внутренние отступы) — группа «Колонки», а не сетка элементов. */
const COLUMN_LOOK = /^columns\.items\.\d+\.(?:bg|place|pad)(?:\.|$)/;

/** Вкладка «Оформление»: порядок — как группы на экране. */
export const LOOK_ISSUE_RULES = [
  { group: "bg", under: ["look.bg"] },
  { group: "size", under: ["look.container", "look.card", "look.height", "look.heightPhone", "look.place", "look.placePhone"] },
  { group: "columns", under: ["columns.shares", "columns.gap", "columns.valign", "columns.elementGap"], exact: ["columns", "columns.items"], match: COLUMN_LOOK },
  { group: "space", under: ["look.space"] },
  { group: "decor", under: ["look.decor"] },
  { group: "phone", under: ["look.visibility", "columns.phoneOrder"] },
] as const satisfies readonly IssueGroupRule<string>[];

/** Вкладка «Содержимое». */
export const CONTENT_ISSUE_RULES = [
  { group: "header", under: ["header"] },
  { group: "grid", under: ["columns.items"], exclude: COLUMN_LOOK },
] as const satisfies readonly IssueGroupRule<string>[];

/**
 * Поля группы «Цвет и шрифт» панели элемента: части текста; у кнопки —
 * заливка и рамка (3а); у таблицы — фон шапки, полосы и линии, у вопросов —
 * разделитель (3б). Значок вопроса тоже там, но ключ `icon` есть и у
 * объявлений (в «Содержимом»), поэтому по пути его не различить — ошибок у
 * значка не бывает (выбор из схем).
 */
export const ELEMENT_STYLE_FIELDS = ["typo", "fill", "border", "head", "stripe", "lines", "divider"] as const;

/** Панель элемента: пути от пути элемента (`columns.items.0.elements.2`). */
export const ELEMENT_ISSUE_RULES = [
  { group: "style", under: ELEMENT_STYLE_FIELDS },
  { group: "place", under: ["hide", "space", "align"] },
  { group: "content", under: [""] },
] as const satisfies readonly IssueGroupRule<string>[];

/** Группы вкладок и панели — из реестров. */
export type LookIssueGroup = (typeof LOOK_ISSUE_RULES)[number]["group"];
export type ContentIssueGroup = (typeof CONTENT_ISSUE_RULES)[number]["group"];
export type ElementIssueGroup = (typeof ELEMENT_ISSUE_RULES)[number]["group"];

/** Полный путь: база + относительный (`""` — сама база). */
function join(base: string, rel: string): string {
  if (!base) return rel;
  return rel ? `${base}.${rel}` : base;
}

function isUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}.`);
}

/** Группа замечания по первому подходящему правилу; `null` — ни одной (замечание не здесь). */
export function issueGroupOf<G extends string>(path: string, rules: readonly IssueGroupRule<G>[], base = ""): G | null {
  // Замечание не под базой (другой элемент) — не наше.
  if (base && !isUnder(path, base)) return null;
  for (const rule of rules) {
    if (rule.exclude?.test(path)) continue;
    if (rule.match?.test(path)) return rule.group;
    if (rule.exact?.some((rel) => path === join(base, rel))) return rule.group;
    if (rule.under?.some((rel) => isUnder(path, join(base, rel)))) return rule.group;
  }
  return null;
}

/** Группы, в которых есть замечания. */
export function groupsWithIssues<G extends string>(paths: readonly string[], rules: readonly IssueGroupRule<G>[], base = ""): ReadonlySet<G> {
  const out = new Set<G>();
  for (const path of paths) {
    const group = issueGroupOf(path, rules, base);
    if (group !== null) out.add(group);
  }
  return out;
}

/** Первая на экране группа с замечанием (`order` — порядок групп на экране); нет — null. */
export function firstIssueGroup<G extends string>(found: ReadonlySet<G>, order: readonly G[]): G | null {
  return order.find((group) => found.has(group)) ?? null;
}
